import { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { apiService } from '../../services/api';

/**
 * Account > Notifications (KAN-151 / KAN-152)
 * Lets a customer choose which order notifications they receive, per store.
 * A toggle that is ON means "receive"; OFF means "muted" for that store.
 * Default is ON: a customer is subscribed to their stores' order notifications unless they
 * mute them. If the customer has globally opted out of email (emailOptOut), the toggles are
 * locked until that opt-out is removed.
 */
const NotificationPreferencesPage = () => {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [catalog, setCatalog] = useState([]);
  const [stores, setStores] = useState([]);

  // clientId -> { [notificationId]: isMuted }
  const [muted, setMuted] = useState({});
  const [original, setOriginal] = useState({});
  const [savingStoreId, setSavingStoreId] = useState(null);
  const [emailOptOut, setEmailOptOut] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await apiService.get('/storefront/notification-preferences');
      const data = res?.data || res;
      const cat = data?.catalog || [];
      const sto = data?.stores || [];

      const initial = {};
      sto.forEach((s) => {
        const mutedSet = new Set(s.mutedNotificationIds || []);
        initial[s.clientId] = {};
        cat.forEach((n) => {
          initial[s.clientId][n.id] = mutedSet.has(n.id);
        });
      });

      setCatalog(cat);
      setStores(sto);
      setMuted(initial);
      setOriginal(JSON.parse(JSON.stringify(initial)));
      setEmailOptOut(!!data?.emailOptOut);
    } catch (err) {
      console.error('Error loading notification preferences:', err);
      setErrorMessage(err?.message || 'Failed to load notification preferences.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (clientId, notificationId) => {
    setSuccessMessage('');
    setMuted((prev) => ({
      ...prev,
      [clientId]: {
        ...prev[clientId],
        // toggle the muted flag (switch ON = receiving = not muted)
        [notificationId]: !prev[clientId]?.[notificationId],
      },
    }));
  };

  const isStoreDirty = (clientId) =>
    JSON.stringify(muted[clientId] || {}) !== JSON.stringify(original[clientId] || {});

  const handleSave = async (clientId) => {
    setSavingStoreId(clientId);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const preferences = catalog.map((n) => ({
        notificationId: n.id,
        isMuted: !!muted[clientId]?.[n.id],
      }));

      await apiService.put('/storefront/notification-preferences', {
        clientId,
        preferences,
      });

      setOriginal((prev) => ({
        ...prev,
        [clientId]: JSON.parse(JSON.stringify(muted[clientId] || {})),
      }));
      setSuccessMessage('Notification preferences saved.');
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      setErrorMessage(err?.message || 'Failed to save notification preferences.');
    } finally {
      setSavingStoreId(null);
    }
  };

  const hasStores = useMemo(() => stores.length > 0, [stores]);

  if (loading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow border-0">
              <Card.Body className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <div className="mt-3">Loading notification preferences...</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <div className="mb-4">
            <h4 className="fw-bold mb-1">Notification Preferences</h4>
            <p className="text-muted mb-0">
              Choose which order notifications you receive for each of your stores. Turning a
              notification off stops the emails for that store only.
            </p>
          </div>

          {successMessage && (
            <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          {emailOptOut && (
            <Alert variant="warning">
              <i className="bi bi-envelope-slash me-2"></i>
              You&apos;re currently opted out of all emails, so order notifications can&apos;t be
              changed here. Update your email preferences (or contact us) to remove the opt-out,
              then you can manage these per store.
            </Alert>
          )}

          {!hasStores && (
            <Card className="shadow-sm border-0">
              <Card.Body className="text-center py-5 text-muted">
                <i className="bi bi-bell-slash fs-1 d-block mb-2 opacity-50"></i>
                You don&apos;t have access to any stores yet.
              </Card.Body>
            </Card>
          )}

          {hasStores &&
            stores.map((store) => (
              <Card key={store.clientId} className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0 fw-bold">
                      <i className="bi bi-shop me-2 text-primary"></i>
                      {store.clientName || `Store #${store.clientId}`}
                    </h6>
                    {store.companyName && (
                      <small className="text-muted">{store.companyName}</small>
                    )}
                  </div>
                  {isStoreDirty(store.clientId) && (
                    <Badge bg="warning" text="dark">
                      Unsaved
                    </Badge>
                  )}
                </Card.Header>
                <Card.Body className="p-4">
                  {catalog.length === 0 ? (
                    <div className="text-muted">No order notifications are available.</div>
                  ) : (
                    catalog.map((n) => (
                      <div
                        key={n.id}
                        className="d-flex justify-content-between align-items-center py-2 border-bottom"
                      >
                        <div className="me-3">
                          <div className="fw-semibold">{n.name}</div>
                          {n.description && (
                            <small className="text-muted">{n.description}</small>
                          )}
                        </div>
                        <Form.Check
                          type="switch"
                          id={`pref-${store.clientId}-${n.id}`}
                          // switch ON = receiving (not muted); forced off while opted out
                          checked={!emailOptOut && !muted[store.clientId]?.[n.id]}
                          onChange={() => handleToggle(store.clientId, n.id)}
                          disabled={emailOptOut}
                          aria-label={`Receive ${n.name} for ${store.clientName}`}
                        />
                      </div>
                    ))
                  )}

                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={emailOptOut || !isStoreDirty(store.clientId) || savingStoreId === store.clientId}
                      onClick={() => handleSave(store.clientId)}
                    >
                      {savingStoreId === store.clientId ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
        </Col>
      </Row>
    </Container>
  );
};

export default NotificationPreferencesPage;
